import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useEvents } from '../lib/hooks';
import type { MenuItem, Order, Pass, PastEvent, Sitting } from '../lib/types';
import {
  dateRangeText, eventEnded, fmtTime, inr, KIND_LABEL, mealName, ORDER_STATUS, passChip, payMethodText, seats, serveDateText, windowText,
} from '../lib/format';
import { Chip, Steps, VegMark } from './ui';

export function orderSteps(o: Order) {
  return [
    { label: 'Booked', state: 'done' as const },
    {
      label: o.pay_mode === 'online' ? 'Paid online' : o.pay_mode === 'cash' ? 'Cash paid' : o.utr || o.legacy_utr_last4 ? 'UTR sent' : 'Paid',
      state: (o.utr || o.legacy_utr_last4 || o.total === 0 || o.cash_recorded_at || o.approved_via || (o.pay_mode === 'online' && o.paid_amount != null) ? 'done' : '') as '' | 'done',
    },
    {
      label: o.status === 'sent_back' ? 'Sent back' : 'Confirmed',
      state: (o.status === 'confirmed' ? 'done' : o.status === 'sent_back' ? 'bad' : '') as '' | 'done' | 'bad',
    },
  ];
}

export function OrderLines({ order, sittings }: { order: Order; sittings: Map<string, Sitting> }) {
  return (
    <ul className="olines">
      {order.lines.map((l) => {
        const s = sittings.get(l.sitting_id);
        return (
          <li key={l.menu_item_id}>
            <span>
              <span><VegMark kind={l.kind} />{s ? `${s.day_label} · ${mealName(s)}` : l.sitting_id}{l.kind !== 'common' ? ` · ${KIND_LABEL[l.kind]}` : ''}</span>
              <small>{seats(l.adults, l.children)}</small>
            </span>
            <b>{l.subtotal ? inr(l.subtotal) : 'Free'}</b>
          </li>
        );
      })}
    </ul>
  );
}

export function OrderCard({ order, sittings, link = true, closed = false }: { order: Order; sittings: Map<string, Sitting>; link?: boolean; closed?: boolean }) {
  // An unpaid order of a closed event is over: nothing to pay or fix.
  const lapsed = closed && (order.status === 'awaiting_payment' || order.status === 'sent_back');
  const st = lapsed ? { label: 'Not paid · event closed', tone: '' as const } : ORDER_STATUS[order.status];
  const body = (
    <article className="card">
      <div className="ocard-top"><span className="mono">{order.code}</span><Chip tone={st.tone}>{st.label}</Chip></div>
      <Steps steps={orderSteps(order)} />
      <OrderLines order={order} sittings={sittings} />
      <div className="ototal">
        <span>{payMethodText(order)}</span>
        <span>Total <b>{inr(order.total)}</b></span>
      </div>
      {order.status === 'sent_back' && order.review_reason && (
        <div className="notice bad"><p><b>Food Committee note:</b> {order.review_reason}</p></div>
      )}
      {link && !lapsed && (order.status === 'awaiting_payment' || order.status === 'sent_back') && (
        <span className="btn primary sm" style={{ justifySelf: 'start' }}>{order.status === 'sent_back' ? 'Fix payment details' : 'Pay and send UTR'}</span>
      )}
    </article>
  );
  return link ? <Link className="card-link" to={`/orders/${encodeURIComponent(order.code)}`}>{body}</Link> : body;
}

/**
 * An order in a list, folded: its code, where it stands and a one-line summary. The meals and payment are one tap away
 * on the order page. A closed event's unpaid order says so instead of asking for payment.
 */
export function OrderRow({ order, closed = false }: { order: Order; closed?: boolean }) {
  const lapsed = closed && (order.status === 'awaiting_payment' || order.status === 'sent_back');
  const st = lapsed ? { label: 'Not paid · event closed', tone: '' as const } : ORDER_STATUS[order.status];
  const meals = new Set(order.lines.map((l) => l.sitting_id)).size;
  const action = !lapsed && (order.status === 'awaiting_payment' ? (order.online_pending ? 'Check payment' : 'Pay now')
    : order.status === 'sent_back' ? 'Fix payment' : null);
  return (
    <Link className={`orow${action ? ' is-todo' : ''}`} to={`/orders/${encodeURIComponent(order.code)}`}>
      <span className="orow-top">
        <span className="mono">{order.code}</span>
        <Chip tone={st.tone}>{st.label}</Chip>
      </span>
      <Steps steps={orderSteps(order)} />
      <span className="orow-meta">
        <span>{meals} meal{meals === 1 ? '' : 's'} · {inr(order.total)} · {payMethodText(order)}</span>
        <span className="orow-go">{action ?? 'Details'} ›</span>
      </span>
    </Link>
  );
}

/**
 * One meal pass in a list. Links to the pass page, or calls onOpen (shared link and committee views). A past event's
 * passes are a record (`link={false}`): no QR to show, so the row says when the seats were served instead.
 */
export function PassRow({ pass, onOpen, link = true }: { pass: Pass; onOpen?: () => void; link?: boolean }) {
  // A closed event's pass says how it was used, not whether it is ready.
  const closed = eventEnded(pass.sitting.event_status);
  const chip = closed && pass.state !== 'fully_served'
    ? { label: pass.state === 'locked' ? 'Not paid' : pass.served > 0 ? `${pass.served} of ${pass.booked} served` : 'Not used', tone: '' as const }
    : passChip(pass);
  const src = pass.state === 'locked'
    ? pass.pending.map((p) => ({ kind: p.kind, a: p.adults, c: p.children }))
    : pass.lines.map((l) => ({ kind: l.kind, a: l.booked_adults, c: l.booked_children }));
  const inner = (
    <>
      <span className="pcard-main">
        <span className="pcard-meal">{mealName(pass.sitting)}</span>
        <span className="pcard-win">{windowText(pass.sitting)}</span>
        <span className="pcard-seats">
          {src.map((x) => {
            const v = pass.venues?.find((y) => y.kind === x.kind);
            return <span key={x.kind}><VegMark kind={x.kind} />{KIND_LABEL[x.kind]} · {seats(x.a, x.c)}{v ? <span className="pcard-venue"> · {v.name}</span> : null}</span>;
          })}
        </span>
        {!link && pass.history.length > 0 && (
          <span className="pcard-win">Served {pass.history.map((h) => `${seats(h.adults, h.children)} at ${fmtTime(h.at)}`).join(', ')}</span>
        )}
      </span>
      <span className="pcard-side"><Chip tone={chip.tone}>{chip.label}</Chip>{link && <span className="chev" aria-hidden="true">›</span>}</span>
    </>
  );
  if (!link) return <div className="pcard is-record">{inner}</div>;
  if (onOpen) return <button type="button" className="pcard" style={{ width: '100%', textAlign: 'left' }} onClick={onOpen}>{inner}</button>;
  return <Link className="pcard" to={`/pass/${pass.sitting.id}`}>{inner}</Link>;
}

export interface EventPasses { id: string; name: string; live: boolean; first: string; passes: Pass[] }

/** Passes split by event: live events first (soonest first), then past events (latest first). */
export function groupByEvent(passes: Pass[]): { current: EventPasses[]; past: EventPasses[] } {
  const map = new Map<string, EventPasses>();
  for (const p of passes) {
    // Passes cached before events existed have no event: they count as current.
    const id = p.sitting.event_id ?? '';
    const g = map.get(id) ?? { id, name: p.sitting.event_name ?? '', live: !eventEnded(p.sitting.event_status), first: p.sitting.opens_at, passes: [] };
    g.passes.push(p);
    if (p.sitting.opens_at < g.first) g.first = p.sitting.opens_at;
    map.set(id, g);
  }
  const all = [...map.values()];
  all.forEach((g) => g.passes.sort((a, b) => a.sitting.opens_at.localeCompare(b.sitting.opens_at)));
  return {
    current: all.filter((g) => g.live).sort((a, b) => a.first.localeCompare(b.first)),
    past: all.filter((g) => !g.live).sort((a, b) => b.first.localeCompare(a.first)),
  };
}

/**
 * A past event, folded to one card: its days and what the family had. The passes and orders open on its own page
 * (`back` is where that page's back link returns to).
 */
export function PastEventCard({ event: e, back }: { event: PastEvent; back: string }) {
  const chip = e.issue || e.checking ? { label: 'Payment being checked', tone: 'warn' as const }
    : e.seats_booked === 0 ? { label: 'Nothing confirmed', tone: '' as const } : null;
  return (
    <Link className="orow past-card" to={`/history/${encodeURIComponent(e.id)}`} state={{ back }}>
      <span className="orow-top">
        <b>{e.name}</b>
        {chip && <Chip tone={chip.tone}>{chip.label}</Chip>}
      </span>
      <span className="orow-meta">
        <span>
          {dateRangeText(e.starts_on, e.ends_on)}
          {e.seats_booked > 0 && ` · ${e.seats_served} of ${e.seats_booked} seats used · ${inr(e.paid)}`}
        </span>
        <span className="orow-go">Passes and orders ›</span>
      </span>
    </Link>
  );
}

/** Passes grouped by day, in sitting order. */
export function groupByDay(passes: Pass[]) {
  const days: { key: string; label: string; bn: string; date: string; passes: Pass[] }[] = [];
  for (const p of passes) {
    const last = days[days.length - 1];
    if (last && last.key === p.sitting.day_key) last.passes.push(p);
    else days.push({ key: p.sitting.day_key, label: p.sitting.day_label, bn: p.sitting.day_label_bn, date: serveDateText(p.sitting), passes: [p] });
  }
  return days;
}

export function ResidentBar({ title, sub, back }: { title?: string; sub?: string; back?: { to: string; label: string } }) {
  const { me, hasRole, signOut } = useAuth();
  const { live } = useEvents();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  // Requests to join or combine with this family, waiting for an answer.
  const inbox = me?.inbox ?? 0;
  if (back) {
    return (
      <div className="app-bar slim">
        <Link className="link-btn" to={back.to}>‹ {back.label}</Link>
        {title && <span className="app-sub">{title}</span>}
      </div>
    );
  }
  return (
    <div className="app-bar">
      <div>
        <p className="app-eyebrow">নমস্কার</p>
        <p className="app-name">{title ?? me?.member?.name ?? me?.flat?.contact_name}</p>
        <p className="app-sub">{sub ?? (me?.flat ? `Tower ${me.flat.tower} · Flat ${me.flat.flat}` : '')}</p>
      </div>
      <div className="menu-pop">
        <button className="btn ghost sm" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          Menu{inbox > 0 && <span className="count-dot" aria-label={`${inbox} waiting`}>{inbox}</span>}
        </button>
        {open && (
          <ul onClick={() => setOpen(false)}>
            <li><Link to="/book">Book meals</Link></li>
            <li><Link to="/venues">Where to eat</Link></li>
            <li><Link to="/account">My family{inbox > 0 && <span className="count-dot">{inbox}</span>}</Link></li>
            <li><Link to="/history">Past events</Link></li>
            {live.map((e) => <li key={e.id}><Link to={`/e/${encodeURIComponent(e.id)}`}>{live.length > 1 ? `${e.name}: programme` : 'Programme'}</Link></li>)}
            {hasRole('committee', 'caterer', 'helpdesk', 'programme') && <li><Link to="/committee">Console</Link></li>}
            {hasRole('caterer', 'committee') && <li><Link to="/counter">Scanner</Link></li>}
            <li><button onClick={() => { void signOut().then(() => navigate('/signin')); }}>Sign out</button></li>
          </ul>
        )}
      </div>
    </div>
  );
}

export function useSittingMap(sittings: Sitting[] | undefined) {
  return new Map((sittings ?? []).map((s) => [s.id, s]));
}

export function menuById(menu: MenuItem[] | undefined) {
  return new Map((menu ?? []).map((m) => [m.id, m]));
}
